// import React from 'react'

// const NewTabWrapper = () => {
//   return (
//     <div>NewTabWrapper</div>
//   )
// }

// export default NewTabWrapper

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";

const TabsURLContext = createContext(null);

const NewTabWrapper = ({ children, paramOrder }) => {
  const subscribersRef = useRef(new Map());
  const registeredKeysRef = useRef(new Set());
  const paramOrderRef = useRef(paramOrder || null);
  const mode = (import.meta.env && import.meta.env.VITE_PAGE_TAB_URL) || "";
  const modeStr = String(mode).toLowerCase();
  const isReadable = modeStr === "readable";
  const isEncoded = ["encoded", "opaque"].includes(modeStr);
  const isHashed = ["non-readable", "sha256"].includes(modeStr);
  const AGG_PARAM = "__tabs";

  const encodeToken = (obj) => {
    try {
      const json = JSON.stringify(obj || {});
      const bytes = new TextEncoder().encode(json);
      let binary = "";
      bytes.forEach((b) => (binary += String.fromCharCode(b)));
      const base64 = btoa(binary)
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/g, "");
      return base64;
    } catch (_) {
      return "";
    }
  };

  const decodeToken = (token) => {
    if (!token) return {};
    try {
      const base64 = token.replace(/-/g, "+").replace(/_/g, "/");
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const json = new TextDecoder().decode(bytes);
      const obj = JSON.parse(json);
      return obj && typeof obj === "object" ? obj : {};
    } catch (_) {
      return {};
    }
  };

  const notifySubscribers = (subscribersMap) => {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const agg = isEncoded ? decodeToken(params.get(AGG_PARAM)) : null;
    subscribersMap.forEach((setCallbacks, key) => {
      const value = isEncoded ? agg[key] ?? null : params.get(key) || null;
      setCallbacks.forEach((cb) => {
        try {
          cb(value);
        } catch (_) {}
      });
    });
  };

  useEffect(() => {
    const onPopState = () => {
      notifySubscribers(subscribersRef.current);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const buildQuery = (params) => {
    const beforeKeys = Array.from(params.keys());
    if (isEncoded || isHashed) {
      const token = params.get(AGG_PARAM);
      return token
        ? `${encodeURIComponent(AGG_PARAM)}=${encodeURIComponent(token)}`
        : "";
    }
    let keys = beforeKeys;
    if (paramOrderRef.current && Array.isArray(paramOrderRef.current)) {
      const ordered = [];
      paramOrderRef.current.forEach((k) => {
        if (params.has(k)) ordered.push(k);
      });
      const remaining = beforeKeys.filter((k) => !ordered.includes(k)).sort();
      keys = [...ordered, ...remaining];
    } else {
      keys = beforeKeys.sort();
    }
    const parts = [];
    keys.forEach((k) => {
      const v = params.get(k);
      if (v !== null)
        parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
    });
    return parts.join("&");
  };

  const toHex = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let hex = "";
    for (let i = 0; i < bytes.length; i++) {
      const h = bytes[i].toString(16).padStart(2, "0");
      hex += h;
    }
    return hex;
  };

  const buildCanonicalString = (obj) => {
    const keys = Object.keys(obj || {});
    let ordered = keys;
    if (paramOrderRef.current && Array.isArray(paramOrderRef.current)) {
      const primary = [];
      paramOrderRef.current.forEach((k) => {
        if (obj.hasOwnProperty(k)) primary.push(k);
      });
      const rest = keys.filter((k) => !primary.includes(k)).sort();
      ordered = [...primary, ...rest];
    } else {
      ordered = keys.sort();
    }
    const parts = ordered.map((k) => `${k}=${obj[k]}`);
    return parts.join("&");
  };

  const value = useMemo(
    () => ({
      getParam: (key) => {
        if (!key) return null;
        const url = new URL(window.location.href);
        if (isHashed) {
          const hash = url.searchParams.get(AGG_PARAM);
          if (!hash) return null;
          try {
            const raw = localStorage.getItem(`tabs:${hash}`);
            if (!raw) return null;
            const obj = JSON.parse(raw);
            const v = obj[key];
            return v === undefined ? null : v;
          } catch (_) {
            return null;
          }
        }
        if (isEncoded) {
          const agg = decodeToken(url.searchParams.get(AGG_PARAM));
          const v = agg[key];
          return v === undefined ? null : v;
        }
        return url.searchParams.get(key);
      },
      setParam: (key, value) => {
        if (!key) return;
        const url = new URL(window.location.href);
        const params = url.searchParams;
        const before = buildQuery(params);
        if (isHashed) {
          try {
            const hash = params.get(AGG_PARAM);
            let obj = {};
            if (hash) {
              const raw = localStorage.getItem(`tabs:${hash}`);
              if (raw) obj = JSON.parse(raw) || {};
            }
            if (value === undefined || value === null || value === "") {
              delete obj[key];
            } else {
              obj[key] = String(value);
            }
            const canonical = buildCanonicalString(obj);
            const enc = new TextEncoder().encode(canonical);
            window.crypto.subtle.digest("SHA-256", enc).then((buf) => {
              const newHash = toHex(buf);
              try {
                localStorage.setItem(`tabs:${newHash}`, JSON.stringify(obj));
              } catch (_) {}
              if (params.has(key)) params.delete(key);
              if (newHash) params.set(AGG_PARAM, newHash);
              else params.delete(AGG_PARAM);
              const query = buildQuery(params);
              const nextURL =
                `${url.pathname}${query ? `?${query}` : ""}` + (url.hash || "");
              if (before !== query) {
                window.history.pushState({}, "", nextURL);
                notifySubscribers(subscribersRef.current);
              }
            });
          } catch (_) {}
        } else if (isEncoded) {
          const agg = decodeToken(params.get(AGG_PARAM));
          if (value === undefined || value === null || value === "") {
            delete agg[key];
          } else {
            agg[key] = String(value);
          }
          const token = encodeToken(agg);
          if (params.has(key)) params.delete(key);
          if (token) params.set(AGG_PARAM, token);
          else params.delete(AGG_PARAM);
        } else {
          if (value === undefined || value === null || value === "") {
            params.delete(key);
          } else {
            params.set(key, String(value));
          }
        }
        const query = buildQuery(params);
        const nextURL =
          `${url.pathname}${query ? `?${query}` : ""}` + (url.hash || "");
        if (!isHashed && before !== query) {
          window.history.pushState({}, "", nextURL);
          notifySubscribers(subscribersRef.current);
        }
      },
      registerKey: (key) => {
        if (!key) return;
        registeredKeysRef.current.add(key);
      },
      unregisterKey: (key) => {
        if (!key) return;
        registeredKeysRef.current.delete(key);
        const url = new URL(window.location.href);
        const params = url.searchParams;
        let changed = false;
        if (isHashed) {
          try {
            const hash = params.get(AGG_PARAM);
            let obj = {};
            if (hash) {
              const raw = localStorage.getItem(`tabs:${hash}`);
              if (raw) obj = JSON.parse(raw) || {};
            }
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
              delete obj[key];
              changed = true;
            }
            const canonical = buildCanonicalString(obj);
            const enc = new TextEncoder().encode(canonical);
            window.crypto.subtle.digest("SHA-256", enc).then((buf) => {
              const newHash = toHex(buf);
              try {
                if (newHash)
                  localStorage.setItem(`tabs:${newHash}`, JSON.stringify(obj));
              } catch (_) {}
              if (newHash) params.set(AGG_PARAM, newHash);
              else params.delete(AGG_PARAM);
              const query = buildQuery(params);
              const nextURL =
                `${url.pathname}${query ? `?${query}` : ""}` + (url.hash || "");
              if (changed) {
                window.history.replaceState({}, "", nextURL);
                notifySubscribers(subscribersRef.current);
              }
            });
          } catch (_) {}
        } else if (isEncoded) {
          const agg = decodeToken(params.get(AGG_PARAM));
          if (Object.prototype.hasOwnProperty.call(agg, key)) {
            delete agg[key];
            changed = true;
          }
          const token = encodeToken(agg);
          if (token) params.set(AGG_PARAM, token);
          else params.delete(AGG_PARAM);
        } else {
          if (!params.has(key)) return;
          params.delete(key);
          changed = true;
        }
        const query = buildQuery(params);
        const nextURL =
          `${url.pathname}${query ? `?${query}` : ""}` + (url.hash || "");
        if (!isHashed && changed) {
          window.history.replaceState({}, "", nextURL);
          notifySubscribers(subscribersRef.current);
        }
      },
      subscribe: (key, callback) => {
        if (!key || typeof callback !== "function") return () => {};
        const map = subscribersRef.current;
        const set = map.get(key) || new Set();
        set.add(callback);
        map.set(key, set);
        return () => {
          const current = map.get(key);
          if (!current) return;
          current.delete(callback);
          if (current.size === 0) map.delete(key);
        };
      },
    }),
    []
  );

  return (
    <TabsURLContext.Provider value={value}>{children}</TabsURLContext.Provider>
  );
};

export const useTabsURL = () => {
  return useContext(TabsURLContext);
};

export default NewTabWrapper;
